import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useFlags } from 'launchdarkly-react-client-sdk';

import { OutlineChip } from '@common';

import {
  ProductTypePropertySlugEnum,
  RelatedProductCategoryNameEnum,
} from '@electricjs/core_entity-client';
import {
  Box,
  Flex,
  IconButton,
  Text,
  Radio,
  RadioGroup,
  Tooltip,
  spaces,
  Icon,
  Collapse,
  CollapseHeader,
  CollapseContent,
} from '@electricjs/arc';

import { useGlobalUI } from '@/components/GlobalUIProvider';
import { useGetOrganizationAdditionalInfoQuery } from '@/redux/slices/organizationApiSlice';
import { useGetOrganizationId } from '@/hooks/useGetOrganizationId';

import formatCurrency from '@/helpers/formatCurrency';

import { AssetCartEntry, CatalogCartEntry, Store } from '@/types/storefront';
import { Product } from '@/types/products';
import { StorefrontAsset } from '@/types/assets';

import { ProductBasicInfo } from './Catalog/ProductBasicInfo';
import { AssetBasicInfo } from './Asset/AssetBasicInfo';
import { ItemDetailsModal } from './ItemDetailsModal';
import { ItemZtpStatus } from './ZTP/ItemZtpStatus';
import { getAutoUpgradedWarranty } from '../utils/cartHelpers';
import { getItemRelatedProducts, isWarehouseAsset } from '../utils/helpers';
import { ItemCarousel } from './ItemCarousel';
import { useStorefront } from '../StorefrontProvider';

const StyledWarrantyList = styled.ul`
  padding-left: 2.4rem;
  margin: 0;
`;

// establishes the containment context: the row's own available width (not the viewport's) is what the layout below reacts to
const ContainerContext = styled(Flex)`
  container-type: inline-size;
`;

// below this width, there isn't enough room to fit price/quantity/total/remove alongside the image+name, so they move to their own row below it
const STACK_CONTAINER_WIDTH = '60rem';

const EntryDataRow = styled(Flex)`
  width: 100%;
  column-gap: 3rem;
  align-items: center;

  @container (max-width: ${STACK_CONTAINER_WIDTH}) {
    flex-direction: column;
    align-items: stretch;
    row-gap: ${spaces.space_m_rem};
  }
`;

const EntryDetailsRow = styled(Flex)`
  column-gap: 3rem;
  row-gap: 1rem;
  flex-wrap: wrap;
  align-items: center;
`;

// the warranty options and the warranty-includes explanation are each full-width, so below the same cramped threshold as the rest of the row, move the explanation underneath the options instead of side-by-side
const WarrantyDetailsRow = styled(Flex)`
  width: 100%;
  column-gap: 2rem;

  @container (max-width: ${STACK_CONTAINER_WIDTH}) {
    flex-direction: column;
    row-gap: 2rem;
  }
`;

export type CatalogCartEntryRow = {
  store: Store.Catalog;
  entry: CatalogCartEntry;
  onEntryRemove: (item: Product) => void;
  onQuantityDecrement: (itemId: string) => void;
  onQuantityIncrement: (itemId: string) => void;
  onWarrantySelect: (itemId: string, warranty: Product) => void;
};

export type AssetCartEntryRow = {
  store: Store.Asset;
  entry: AssetCartEntry;
  onEntryRemove: (item: StorefrontAsset) => void;
  onQuantityDecrement?: never;
  onQuantityIncrement?: never;
  onWarrantySelect?: never;
};

export type CartEntryRowProps = CatalogCartEntryRow | AssetCartEntryRow;

export const CartEntryRow = ({
  store,
  entry,
  onQuantityDecrement,
  onQuantityIncrement,
  onWarrantySelect,
  onEntryRemove,
}: CartEntryRowProps) => {
  const { t } = useTranslation('storefront');
  const { openModal, closeModal } = useGlobalUI();
  const {
    warrantyUpgradePreference: warrantyUpgradePreferenceEnabled,
    storefrontWarehouseShippingFee,
    storefrontRecommendedAccessoriesInCart,
  } = useFlags();

  const {
    catalogCart,
    storefrontCheckoutForm,
    addCatalogCartEntry,
    removeCatalogCartEntry,
  } = useStorefront();

  const isEmployeeSelected = Boolean(
    storefrontCheckoutForm?.getValues('employee')?.value
  );

  const organizationId = useGetOrganizationId();
  const { data: orgAdditionalInfo } = useGetOrganizationAdditionalInfoQuery({
    organizationId,
  });

  const isWarrantyAutoUpgradeEnabled =
    warrantyUpgradePreferenceEnabled &&
    orgAdditionalInfo?.warrantyUpgradePreference;

  const getEntryTotalPrice = (entry: CatalogCartEntry) => {
    const warrantyPrice = entry.selectedWarranty?.price ?? 0;

    return ((entry.item.price ?? 0) + warrantyPrice) * entry.quantity;
  };

  const sortWarrantiesByDefaultFirst = (
    warranties: Product[],
    defaultWarrantyId?: string
  ) =>
    warranties.toSorted((a, b) => {
      if (a.id === defaultWarrantyId && b.id !== defaultWarrantyId) {
        return -1;
      }
      if (a.id !== defaultWarrantyId && b.id === defaultWarrantyId) {
        return 1;
      }
      return 0;
    });

  const quantityLimitReached =
    store === Store.Catalog &&
    entry.item.windowsZtpLowestInventory !== undefined &&
    entry.quantity >= entry.item.windowsZtpLowestInventory;

  return (
    <ContainerContext vertical rowGap={spaces.space_l}>
      <EntryDataRow>
        {/* Item data (e.g., image, name) */}
        <Box flexGrow="1">
          {store === Store.Catalog && (
            <ProductBasicInfo
              name={entry.item.name}
              image={entry.item.image}
              onLinkClick={() =>
                openModal(
                  <ItemDetailsModal
                    item={entry.item}
                    store={Store.Catalog}
                    onAddItem={() => {}}
                    onRemoveItem={() => onEntryRemove(entry.item)}
                    onClose={closeModal}
                    selected
                  />
                )
              }
            />
          )}
          {store === Store.Asset && (
            <AssetBasicInfo
              name={entry.item.name}
              customId={entry.item.customAssetId}
              serialNumber={entry.item.serialNumber}
              siteName={entry.item.site?.name}
              onLinkClick={() =>
                openModal(
                  <ItemDetailsModal
                    item={entry.item}
                    store={Store.Asset}
                    onAddItem={() => {}}
                    onRemoveItem={() => onEntryRemove(entry.item)}
                    onClose={closeModal}
                    selected
                  />
                )
              }
            />
          )}
        </Box>

        <EntryDetailsRow>
          {/* Item price (unit price) */}
          {store === Store.Catalog && (
            <Text
              flexBasis="12rem"
              flexShrink="0"
              variant="body"
              textAlign="end">
              {entry.item.price === undefined
                ? t('sections.cart.cartEntry.priceUnavailable')
                : formatCurrency(entry.item.price, entry.item.currencyCode)}
            </Text>
          )}

          {/* Quantity selectors */}
          {store === Store.Catalog && (
            <Flex flexBasis="9rem" flexShrink="0" alignItems="center">
              <IconButton
                id={`cart-entry-decrease-quantity-button-${entry.item.id}`}
                icon="minus-circle"
                iconSize="medium"
                disabled={entry.quantity === 1}
                onClick={() => onQuantityDecrement(entry.item.id)}
              />
              <Text flexBasis="2rem" textAlign="center" variant="label-large">
                {entry.quantity}
              </Text>
              <Tooltip
                maxWidth="20rem"
                content={t('sections.cart.cartEntry.quantityTooltip')}
                disabled={!quantityLimitReached} // hide tooltip while quantity limit is not reached
                placement="top-start">
                <IconButton
                  id={`cart-entry-increase-quantity-button-${entry.item.id}`}
                  icon="add-circle"
                  iconSize="medium"
                  disabled={quantityLimitReached}
                  onClick={() => onQuantityIncrement(entry.item.id)}
                />
              </Tooltip>
            </Flex>
          )}

          {/* Entry total price */}
          {store === Store.Catalog && (
            <Text
              flexBasis="13rem"
              flexShrink="0"
              variant="label-large"
              textAlign="end">
              {entry.item.price === undefined
                ? t('sections.cart.cartEntry.priceUnavailable')
                : formatCurrency(
                    getEntryTotalPrice(entry),
                    entry.item.currencyCode
                  )}
            </Text>
          )}

          {/* Warehouse shipping */}
          {store === Store.Asset &&
            isWarehouseAsset(entry) &&
            storefrontWarehouseShippingFee && (
              <OutlineChip
                id={`warehouse-shipping-cost-chip-${entry.item.id}`}
                label={t('common:storefront.asset.shippingStartingAt')}
                variant="small"
              />
            )}

          {/* Self-ship */}
          {store === Store.Asset &&
            !isWarehouseAsset(entry) &&
            storefrontWarehouseShippingFee && (
              <OutlineChip
                id={`self-ship-chip-${entry.item.id}`}
                label={t('common:storefront.asset.selfShipChipLabel')}
                variant="small"
              />
            )}

          {/* Entry remove button */}
          <IconButton
            flexBasis="3.4rem"
            id={`cart-entry-remove-button-${entry.item.id}`}
            icon="trashcan"
            testId="remove-entry"
            onClick={() =>
              store === Store.Catalog
                ? onEntryRemove(entry.item as Product)
                : onEntryRemove(entry.item as StorefrontAsset)
            }
          />
        </EntryDetailsRow>
      </EntryDataRow>

      {/* ZTP status */}
      {store === Store.Catalog && <ItemZtpStatus item={entry.item} />}

      {/* Warranty Options */}
      {store === Store.Catalog &&
        entry.item?.availableWarranties?.list &&
        entry.item.availableWarranties.list.length > 0 && (
          <Flex vertical width="100%" rowGap="2rem">
            {/* Title */}
            <Text variant="label-large">
              {t('sections.cart.cartEntry.upgradeWarranty')}
            </Text>

            {/* Warranty */}
            <WarrantyDetailsRow>
              {/* Warranty Options */}
              <Flex width="100%" vertical rowGap="2rem">
                <RadioGroup aria-label="warranty-selection-options" stack>
                  {sortWarrantiesByDefaultFirst(
                    entry.item.availableWarranties.list,
                    entry.item?.availableWarranties?.defaultWarrantyId
                  ).map(warranty => {
                    // for each warranty available for the product
                    const isWarrantyDefault =
                      warranty.id ===
                      entry.item?.availableWarranties?.defaultWarrantyId;

                    const isPriceUnavailable =
                      !isWarrantyDefault && warranty.price === undefined;

                    const isWarrantySelected =
                      warranty.id === entry.selectedWarranty?.id;

                    return (
                      <Radio
                        id={`${entry.item.id}-warranty-${warranty.id}`}
                        key={`warranty-${warranty.id}`}
                        value={warranty.price ?? 0}
                        checked={isWarrantySelected}
                        disabled={isPriceUnavailable}
                        onChange={() =>
                          onWarrantySelect(entry.item.id, warranty)
                        }>
                        <Text>
                          {warranty.name} -{' '}
                          <Text variant="label-large">
                            {isWarrantyDefault
                              ? t('sections.cart.cartEntry.warrantyIncluded')
                              : isPriceUnavailable
                                ? t('sections.cart.cartEntry.priceUnavailable')
                                : formatCurrency(
                                    warranty.price,
                                    warranty.currencyCode
                                  )}
                          </Text>
                        </Text>
                      </Radio>
                    );
                  })}
                </RadioGroup>
                {isWarrantyAutoUpgradeEnabled &&
                  entry.selectedWarranty?.id !==
                    getAutoUpgradedWarranty(
                      entry.item.availableWarranties?.list ?? [],
                      entry.item.availableWarranties?.defaultWarrantyId
                    )?.id && (
                    <Flex gap="1rem" ml={spaces.space_xs}>
                      <Icon
                        icon="triangle-exclamation"
                        size="small"
                        intent="warning"
                      />
                      <Text variant="legal">
                        {t(
                          'sections.cart.cartEntry.warrantyPreferenceDeviation'
                        )}
                      </Text>
                    </Flex>
                  )}
              </Flex>

              {/* Warranty Includes */}
              {entry.selectedWarranty && (
                <Flex vertical width="100%">
                  <Text mb="2rem">
                    {t('sections.cart.cartEntry.warrantyIncludes', {
                      warrantyName: entry.selectedWarranty.name,
                    })}
                  </Text>
                  <StyledWarrantyList>
                    {entry.selectedWarranty.dynamicProperties
                      ?.find(
                        prop =>
                          prop.slug ===
                          ProductTypePropertySlugEnum.WarrantyCoverage
                      )
                      ?.value?.split(';')
                      .map(item => (
                        <li key={`warranty-include-${item}`}>
                          <Text>{item.trim()}</Text>
                        </li>
                      ))}
                  </StyledWarrantyList>
                </Flex>
              )}
            </WarrantyDetailsRow>
          </Flex>
        )}

      {/* Recommended accessories (it will work for both assets or products in the cart, even though the backend still doesn't have a relatedProducts property for assets) */}
      {storefrontRecommendedAccessoriesInCart &&
        getItemRelatedProducts(
          entry.item,
          RelatedProductCategoryNameEnum.Accessories
        ).length > 0 && (
          <Box width="100%">
            <Collapse defaultVisible>
              <CollapseHeader>
                {t('sections.cart.cartEntry.recommendedAccessories')}
              </CollapseHeader>
              <CollapseContent>
                <ItemCarousel
                  id={`recommended-products-in-cart-for-${entry.item.id}`}
                  store={Store.Catalog} // recommending only purchasable items (not items from their own inventory)
                  items={getItemRelatedProducts(
                    entry.item,
                    RelatedProductCategoryNameEnum.Accessories
                  )}
                  selectedItemsIds={catalogCart.map(entry => entry.item.id)}
                  onAddItem={addCatalogCartEntry}
                  onRemoveItem={removeCatalogCartEntry}
                  isEmployeeSelected={isEmployeeSelected}
                />
              </CollapseContent>
            </Collapse>
          </Box>
        )}
    </ContainerContext>
  );
};
